/**
 * Alta masiva por CSV pegado (T5): el usuario pega lo que copió de una planilla (separador
 * autodetectado, cabeceras con alias en español/inglés — shared/csv.ts), ve la vista previa con
 * los errores por fila y confirma. Una fila mala no aborta las demás: el resultado vuelve por fila.
 * Genérico: quien lo usa pasa `fields` (alias), `required`, `toRow` (fila CSV → objeto del server o
 * un error) y `onImport`. La vista previa muestra los campos del objeto resultante: NO usar este diálogo
 * con filas que traigan secretos (el ImportDialog del Vault migra acá cuando tenga `renderDetail`,
 * tandas con progreso, `maxRows` y vaciado del texto al terminar — deuda anotada en el catálogo).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { InternalTable } from '@/components/internal/shared';
import { EnumBadge } from '@/components/ui/enum-badge';
import { parseCsvTable } from '../../../../shared/csv';

export interface CsvImportResult {
  imported: number;
  results?: Array<{ row: number; ok: boolean; error?: string }>;
}

/** `a: 1 · b: x` — los campos no vacíos del objeto, legibles (arrays unidos por coma). */
const describe = (v: unknown): string =>
  v && typeof v === 'object'
    ? Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== null && x !== undefined && x !== '' && !(Array.isArray(x) && !x.length)).map(([k, x]) => `${k}: ${Array.isArray(x) ? x.join(', ') : String(x)}`).join(' · ')
    : String(v);

export function CsvImportDialog<K extends string, R>({ open, onOpenChange, title, description, fields, required, templateHeader, toRow, onImport, pending }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  fields: Record<K, readonly string[]>;
  required: readonly K[];
  /** Línea de cabecera sugerida (se muestra como placeholder). */
  templateHeader: string;
  /** Fila parseada → objeto para el server, o `{ error }`. */
  toRow: (row: Partial<Record<K, string>>, index: number) => { value: R } | { error: string };
  onImport: (rows: R[]) => Promise<CsvImportResult>;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [result, setResult] = useState<CsvImportResult | null>(null);
  const parsed = useMemo(() => {
    if (!text.trim()) return null;
    const table = parseCsvTable<K>(text, fields, required as readonly K[]);
    const rows = table.rows.map((r, i) => ({ index: i + 1, ...toRow(r, i) }));
    return { table, rows, unknownHeaders: table.headers.filter((h) => !h.field).map((h) => h.raw) };
  }, [text, fields, required, toRow]);
  const valid = parsed?.rows.filter((r): r is { index: number; value: R } => 'value' in r) ?? [];
  const invalid = parsed?.rows.filter((r): r is { index: number; error: string } => 'error' in r) ?? [];
  const submit = async () => {
    if (!valid.length || pending) return;
    try {
      setResult(await onImport(valid.map((v) => v.value)));
    } catch {
      /* el caller ya avisó con toast; la vista previa queda para corregir y reintentar */
    }
  };
  const close = (o: boolean) => {
    if (!o) { setText(''); setResult(null); }
    onOpenChange(o);
  };
  type Row = { index: number } & ({ value: R } | { error: string });
  const cols = [
    { key: 'n', label: '#', render: (r: Row) => r.index },
    { key: 'status', label: t('org.csv.col_status', 'Estado'), render: (r: Row) => ('error' in r ? <EnumBadge value="error" tone="danger" label={t('org.csv.row_error', 'Error')} /> : <EnumBadge value="ok" tone="success" label={t('org.csv.row_ok', 'Lista')} />) },
    { key: 'detail', label: t('org.csv.col_detail', 'Detalle'), render: (r: Row) => <span className="text-muted-foreground">{'error' in r ? r.error : describe(r.value)}</span> },
  ];
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-2 text-sm">
            <p>{t('org.csv.done', 'Importadas: {{n}}', { n: result.imported })}</p>
            {result.results?.filter((r) => !r.ok).length ? (
              <ul className="list-disc pl-5 text-muted-foreground">{result.results.filter((r) => !r.ok).map((r) => <li key={r.row}>#{r.row}: {r.error}</li>)}</ul>
            ) : null}
          </div>
        ) : (
          <div className="space-y-3">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder={templateHeader} className="font-mono text-xs" />
            {parsed && parsed.table.missing.length > 0 && <p className="text-sm text-destructive">{t('org.csv.missing', 'Faltan columnas: {{cols}}', { cols: parsed.table.missing.join(', ') })}</p>}
            {parsed && parsed.unknownHeaders.length > 0 && <p className="text-xs text-muted-foreground">{t('org.csv.unknown', 'Columnas ignoradas: {{cols}}', { cols: parsed.unknownHeaders.join(', ') })}</p>}
            {parsed && parsed.rows.length > 0 && (
              <div className="max-h-72 overflow-auto rounded-md border">
                <InternalTable columns={cols} rows={parsed.rows as Row[]} rowKey={(r) => String(r.index)} />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>{result ? t('common.close', 'Cerrar') : t('common.cancel', 'Cancelar')}</Button>
          {!result && <Button onClick={submit} disabled={pending || !valid.length || !!parsed?.table.missing.length}>{t('org.csv.submit', 'Importar {{n}} filas', { n: valid.length })}{invalid.length ? ` (${invalid.length} ${t('org.csv.with_errors', 'con error')})` : ''}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
